import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../components/ui/sonner';
import { Logo } from '../components/Logo';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RESET_PASSWORD } from '../constants/testIds';
import { getErrorMessage } from '../lib/errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password reset — you can now log in');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-brand-bg text-brand-text flex items-center justify-center px-5">
        <div className="text-center max-w-sm w-full">
          <div className="flex justify-center mb-8">
            <Logo />
          </div>
          <p data-testid={RESET_PASSWORD.invalidTokenMessage} className="text-brand-muted text-sm">
            This reset link is missing or invalid. Please request a new one.
          </p>
          <p className="mt-6 text-brand-muted text-xs">
            <Link to="/forgot-password" className="text-brand-amber underline">Request a new link</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text flex items-center justify-center px-5">
      <div className="text-center max-w-sm w-full">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        <h1 className="font-display font-extrabold uppercase text-2xl text-brand-text">Reset Password</h1>
        <p className="mt-2 text-brand-muted text-sm">Choose a new password for your account.</p>

        {done ? (
          <p data-testid={RESET_PASSWORD.successMessage} className="mt-8 text-brand-amber text-sm">
            Your password has been reset. Redirecting you to log in…
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4 text-left">
            <div>
              <Label className="text-brand-muted text-xs uppercase tracking-wider">New Password</Label>
              <Input
                type="password"
                required
                data-testid={RESET_PASSWORD.passwordInput}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text"
              />
            </div>
            <div>
              <Label className="text-brand-muted text-xs uppercase tracking-wider">Confirm New Password</Label>
              <Input
                type="password"
                required
                data-testid={RESET_PASSWORD.confirmInput}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid={RESET_PASSWORD.submitButton}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 transition-colors disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Reset Password'}
            </button>
          </form>
        )}

        <p className="mt-6 text-brand-muted text-xs">
          <Link data-testid={RESET_PASSWORD.loginLink} to="/login" className="text-brand-amber underline">
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
}
